import { useState, useEffect, useRef } from 'react'
import { Plus, Edit, Trash2, Save, X, Calendar, Clock, Image as ImageIcon, Sparkles, Star, Coffee, Upload, UtensilsCrossed } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { apiFetch, getEventImageUrl } from '../../config/api'
import PageHeader from '../../components/Dashboard/PageHeader'
import type { BrunchMenuHighlights } from '../../services/brunchService'
import { EMPTY_MENU_HIGHLIGHTS, normalizeMenuHighlights } from '../../services/brunchService'

interface Brunch {
  id?: number
  title: string
  slug?: string
  subtitle: string
  event_date: string
  event_time: string
  price?: string | null
  description: string
  long_description: string
  image_url: string | null
  preview_image_url: string | null
  flyer_image_url: string | null
  icon_name: string
  features: string[]
  menu_highlights: BrunchMenuHighlights
  is_highlighted: boolean
  is_active: boolean
}

const MENU_CATEGORIES: { key: keyof BrunchMenuHighlights; label: string }[] = [
  { key: 'fruehstueck', label: 'Frühstück' },
  { key: 'vorspeise', label: 'Vorspeise' },
  { key: 'hauptspeise', label: 'Hauptspeise' },
  { key: 'dessert', label: 'Dessert' },
]

const iconOptions = ['Heart', 'Flower2', 'TreePine', 'Egg', 'Coffee', 'UtensilsCrossed', 'Sparkles']

export default function BrunchManagement() {
  const [brunches, setBrunches] = useState<Brunch[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingBrunch, setEditingBrunch] = useState<Brunch | null>(null)
  const [uploadingPreview, setUploadingPreview] = useState(false)
  const [uploadingFlyer, setUploadingFlyer] = useState(false)
  const [previewImagePreview, setPreviewImagePreview] = useState<string | null>(null)
  const [flyerImagePreview, setFlyerImagePreview] = useState<string | null>(null)
  const previewFileInputRef = useRef<HTMLInputElement>(null)
  const flyerFileInputRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLDivElement>(null)

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    event_date: '',
    event_time: '11:00 - 15:00 Uhr',
    price: '',
    description: '',
    long_description: '',
    image_url: '',
    preview_image_url: '',
    flyer_image_url: '',
    icon_name: 'Coffee',
    features: [''],
    menu_highlights: { ...EMPTY_MENU_HIGHLIGHTS },
    is_highlighted: false,
    is_active: true
  })

  useEffect(() => {
    fetchBrunches()
  }, [])

  const getAuthToken = () => {
    return localStorage.getItem('dashboard_token') || ''
  }

  const fetchBrunches = async () => {
    try {
      setIsLoading(true)
      setError('')
      
      const token = getAuthToken()
      console.log('Fetching brunches with token:', token ? 'Token vorhanden' : 'Kein Token')
      
      const response = await apiFetch('/brunches', {
        headers: {
          'Authorization': token,
        },
      })

      console.log('Response status:', response.status)
      console.log('Response ok:', response.ok)
      
      // Prüfe Content-Type
      const contentType = response.headers.get('content-type')
      console.log('Content-Type:', contentType)
      
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text()
        console.error('Non-JSON response:', text.substring(0, 500))
        setError(`Ungültige Antwort vom Server: ${response.status}`)
        setIsLoading(false)
        return
      }

      const data = await response.json()
      console.log('Response data:', data)
      
      if (response.ok) {
        const brunchesArray = data.success && data.data ? data.data : (Array.isArray(data) ? data : [])
        console.log('Brunches loaded:', brunchesArray.length)
        setBrunches(brunchesArray)
      } else {
        setError(data.error || 'Fehler beim Laden der Brunches')
      }
    } catch (err: any) {
      console.error('Fetch Error:', err)
      setError(`Verbindungsfehler beim Laden der Brunches: ${err.message || 'Unbekannter Fehler'}`)
    } finally {
      setIsLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      event_date: '',
      event_time: '11:00 - 15:00 Uhr',
      price: '',
      description: '',
      long_description: '',
      image_url: '',
      preview_image_url: '',
      flyer_image_url: '',
    icon_name: 'Coffee',
    features: [''],
    menu_highlights: { ...EMPTY_MENU_HIGHLIGHTS },
    is_highlighted: false,
    is_active: true
  })
  setPreviewImagePreview(null)
    setFlyerImagePreview(null)
    if (previewFileInputRef.current) previewFileInputRef.current.value = ''
    if (flyerFileInputRef.current) flyerFileInputRef.current.value = ''
  }

  const cancelEdit = () => {
    setEditingBrunch(null)
    resetForm()
  }

  const handleAddBrunch = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.title || !formData.event_date) {
      setError('Titel und Datum sind erforderlich.')
      return
    }

    try {
      const response = await apiFetch('/brunches', {
        method: 'POST',
        headers: {
          'Authorization': getAuthToken(),
        },
        body: JSON.stringify({
          title: formData.title,
          subtitle: formData.subtitle,
          event_date: formData.event_date,
          event_time: formData.event_time,
          price: formData.price && formData.price.trim() !== '' ? formData.price.trim() : null,
          description: formData.description,
          long_description: formData.long_description,
          image_url: formData.image_url || null,
          preview_image_url: formData.preview_image_url || null,
          flyer_image_url: formData.flyer_image_url || null,
          icon_name: formData.icon_name,
          features: formData.features.filter(f => f.trim() !== ''),
          menu_highlights: {
            fruehstueck: formData.menu_highlights.fruehstueck.filter(m => m.trim() !== ''),
            vorspeise: formData.menu_highlights.vorspeise.filter(m => m.trim() !== ''),
            hauptspeise: formData.menu_highlights.hauptspeise.filter(m => m.trim() !== ''),
            dessert: formData.menu_highlights.dessert.filter(m => m.trim() !== ''),
          },
          is_highlighted: formData.is_highlighted,
          is_active: formData.is_active,
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setShowAddForm(false)
        resetForm()
        fetchBrunches()
      } else {
        setError(data.error || 'Fehler beim Erstellen des Brunchs')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Add brunch error:', err)
    }
  }

  const handleUpdateBrunch = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.title || !formData.event_date) {
      setError('Titel und Datum sind erforderlich.')
      return
    }

    if (!editingBrunch || !editingBrunch.id) return

    try {
      const response = await apiFetch('/brunches', {
        method: 'PUT',
        headers: {
          'Authorization': getAuthToken(),
        },
        body: JSON.stringify({
          id: editingBrunch.id,
          title: formData.title,
          subtitle: formData.subtitle,
          event_date: formData.event_date,
          event_time: formData.event_time,
          price: formData.price && formData.price.trim() !== '' ? formData.price.trim() : null,
          description: formData.description,
          long_description: formData.long_description,
          image_url: formData.image_url || null,
          preview_image_url: formData.preview_image_url || null,
          flyer_image_url: formData.flyer_image_url || null,
          icon_name: formData.icon_name,
          features: formData.features.filter(f => f.trim() !== ''),
          menu_highlights: {
            fruehstueck: formData.menu_highlights.fruehstueck.filter(m => m.trim() !== ''),
            vorspeise: formData.menu_highlights.vorspeise.filter(m => m.trim() !== ''),
            hauptspeise: formData.menu_highlights.hauptspeise.filter(m => m.trim() !== ''),
            dessert: formData.menu_highlights.dessert.filter(m => m.trim() !== ''),
          },
          is_highlighted: formData.is_highlighted,
          is_active: formData.is_active,
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setEditingBrunch(null)
        resetForm()
        fetchBrunches()
      } else {
        setError(data.error || 'Fehler beim Aktualisieren des Brunchs')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Update brunch error:', err)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Möchten Sie diesen Brunch wirklich löschen?')) return

    try {
      setError('')
      
      const response = await apiFetch(`/brunches?id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': getAuthToken(),
        },
      })

      const data = await response.json()

      if (response.ok && data.success) {
        await fetchBrunches()
      } else {
        setError(data.error || 'Fehler beim Löschen des Brunchs')
      }
    } catch (err) {
      setError('Verbindungsfehler beim Löschen')
      console.error('Delete Error:', err)
    }
  }

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'preview' | 'flyer'
  ) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onloadend = () => {
      if (type === 'preview') {
        setPreviewImagePreview(reader.result as string)
      } else {
        setFlyerImagePreview(reader.result as string)
      }
    }
    reader.readAsDataURL(file)

    try {
      if (type === 'preview') {
        setUploadingPreview(true)
      } else {
        setUploadingFlyer(true)
      }
      setError('')

      const uploadFormData = new FormData()
      uploadFormData.append('image', file)
      uploadFormData.append('image_type', type)
      uploadFormData.append('alt_text', formData.title || `Brunch ${type === 'preview' ? 'Vorschaubild' : 'Flyer'}`)

      const apiBase = import.meta.env.VITE_API_BASE_URL || '/api'
      const response = await fetch(`${apiBase}/brunch_images.php`, {
        method: 'POST',
        headers: {
          'Authorization': getAuthToken(),
        },
        body: uploadFormData,
      })

      const data = await response.json()

      if (response.ok && data.success) {
        const imagePath = data.path || data.file_path
        
        if (type === 'preview') {
          setFormData(prev => ({ ...prev, preview_image_url: imagePath }))
        } else {
          setFormData(prev => ({ ...prev, flyer_image_url: imagePath }))
        }
      } else {
        setError(data.error || 'Fehler beim Hochladen des Bildes')
        if (type === 'preview') {
          setPreviewImagePreview(null)
        } else {
          setFlyerImagePreview(null)
        }
      }
    } catch (err) {
      setError('Verbindungsfehler beim Hochladen')
      if (type === 'preview') {
        setPreviewImagePreview(null)
      } else {
        setFlyerImagePreview(null)
      }
      console.error('Upload error:', err)
    } finally {
      if (type === 'preview') {
        setUploadingPreview(false)
      } else {
        setUploadingFlyer(false)
      }
    }
  }

  const startEdit = (brunch: Brunch) => {
    setEditingBrunch(brunch)
    setShowAddForm(false)
    setFormData({
      title: brunch.title,
      subtitle: brunch.subtitle,
      event_date: brunch.event_date,
      event_time: brunch.event_time,
      price: brunch.price || '',
      description: brunch.description,
      long_description: brunch.long_description,
      image_url: brunch.image_url || '',
      preview_image_url: brunch.preview_image_url || '',
      flyer_image_url: brunch.flyer_image_url || '',
      icon_name: brunch.icon_name,
      features: brunch.features && brunch.features.length > 0 ? brunch.features : [''],
      menu_highlights: brunch.menu_highlights ? normalizeMenuHighlights(brunch.menu_highlights) : { ...EMPTY_MENU_HIGHLIGHTS },
      is_highlighted: brunch.is_highlighted,
      is_active: brunch.is_active,
    })
    setPreviewImagePreview(null)
    setFlyerImagePreview(null)
    if (previewFileInputRef.current) previewFileInputRef.current.value = ''
    if (flyerFileInputRef.current) flyerFileInputRef.current.value = ''
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  const addFeature = () => {
    setFormData(prev => ({ ...prev, features: [...prev.features, ''] }))
  }

  const updateFeature = (index: number, value: string) => {
    const newFeatures = [...formData.features]
    newFeatures[index] = value
    setFormData(prev => ({ ...prev, features: newFeatures }))
  }

  const removeFeature = (index: number) => {
    const newFeatures = formData.features.filter((_, i) => i !== index)
    setFormData(prev => ({ ...prev, features: newFeatures.length > 0 ? newFeatures : [''] }))
  }

  const addMenuItem = (category: keyof BrunchMenuHighlights) => {
    setFormData(prev => ({
      ...prev,
      menu_highlights: {
        ...prev.menu_highlights,
        [category]: [...prev.menu_highlights[category], ''],
      },
    }))
  }

  const updateMenuItem = (category: keyof BrunchMenuHighlights, index: number, value: string) => {
    const items = [...formData.menu_highlights[category]]
    items[index] = value
    setFormData(prev => ({
      ...prev,
      menu_highlights: { ...prev.menu_highlights, [category]: items },
    }))
  }

  const removeMenuItem = (category: keyof BrunchMenuHighlights, index: number) => {
    const items = formData.menu_highlights[category].filter((_, i) => i !== index)
    setFormData(prev => ({
      ...prev,
      menu_highlights: { ...prev.menu_highlights, [category]: items },
    }))
  }

  return (
    <div className="w-full min-w-0 max-w-full">
      <PageHeader
        title="Brunch"
        description="Saisonale Brunch-Events anlegen und pflegen."
        actions={
          !showAddForm && !editingBrunch ? (
            <button
              onClick={() => {
                setShowAddForm(true)
                setEditingBrunch(null)
                resetForm()
                setTimeout(() => {
                  formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }, 100)
              }}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-brand-800 text-white text-sm font-medium hover:bg-brand-700"
            >
              <Plus className="h-4 w-4" />
              Neuer Brunch
            </button>
          ) : undefined
        }
      />

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400"
        >
          {error}
        </motion.div>
      )}

      {/* Add/Edit Form */}
      {(showAddForm || editingBrunch) && (
        <motion.div
          ref={formRef}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 relative bg-gradient-to-br from-gray-900/80 to-gray-900/80 backdrop-blur-xl rounded-xl shadow-lg border border-white/10 overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />
          
          <div className="relative z-10 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white">
                {editingBrunch ? 'Brunch bearbeiten' : 'Neuen Brunch erstellen'}
              </h2>
              <motion.button
                onClick={() => {
                  setShowAddForm(false)
                  cancelEdit()
                }}
                className="text-gray-400 hover:text-white transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="h-5 w-5" />
              </motion.button>
            </div>

            <form onSubmit={editingBrunch ? handleUpdateBrunch : handleAddBrunch} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Titel *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Untertitel
                  </label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Datum *
                  </label>
                  <input
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Uhrzeit
                  </label>
                  <input
                    type="text"
                    value={formData.event_time}
                    onChange={(e) => setFormData({ ...formData, event_time: e.target.value })}
                    placeholder="z.B. 11:00 - 15:00 Uhr"
                    className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Preis
                  </label>
                  <input
                    type="text"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="z.B. 45,00 € oder Preis auf Anfrage"
                    className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Icon
                  </label>
                  <select
                    value={formData.icon_name}
                    onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white focus:outline-none focus:border-brand-500/50"
                  >
                    {iconOptions.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.is_highlighted}
                      onChange={(e) => setFormData({ ...formData, is_highlighted: e.target.checked })}
                      className="w-5 h-5 bg-gray-900/50 border border-white/10 rounded text-brand-600 focus:ring-brand-500 focus:ring-offset-0"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                        Als "Beliebt" markieren ⭐
                      </div>
                    </div>
                  </label>
                </div>

                <div>
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-5 h-5 bg-gray-900/50 border border-white/10 rounded text-brand-600 focus:ring-brand-500 focus:ring-offset-0"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                        Aktiv (sichtbar auf Website)
                      </div>
                    </div>
                  </label>
                </div>

              </div>

              {/* Vorschaubild */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Vorschaubild <span className="text-xs text-gray-400">(für /brunch Übersicht)</span>
                </label>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <motion.label
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 border border-white/10 rounded-lg text-white cursor-pointer hover:bg-gray-800 transition-colors"
                    >
                      <Upload className="h-4 w-4" />
                      <span className="text-sm">
                        {uploadingPreview ? 'Wird hochgeladen...' : 'Vorschaubild hochladen'}
                      </span>
                      <input
                        ref={previewFileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'preview')}
                        disabled={uploadingPreview}
                        className="hidden"
                      />
                    </motion.label>
                    
                    {formData.preview_image_url && (
                      <span className="text-xs text-gray-400">
                        {formData.preview_image_url}
                      </span>
                    )}
                  </div>

                  {(previewImagePreview || formData.preview_image_url) && (
                    <div className="relative w-[200px] h-[140px] flex-shrink-0">
                      <div className="relative w-full h-full overflow-hidden rounded-lg border border-white/10 bg-gray-900">
                        <img
                          src={(previewImagePreview && (previewImagePreview.startsWith('blob:') || previewImagePreview.startsWith('data:'))) ? previewImagePreview : getEventImageUrl(formData.preview_image_url)}
                          alt="Vorschaubild Vorschau"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {formData.preview_image_url && (
                        <motion.button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, preview_image_url: '' }))
                            setPreviewImagePreview(null)
                            if (previewFileInputRef.current) {
                              previewFileInputRef.current.value = ''
                            }
                          }}
                          className="absolute top-1 right-1 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-full text-white"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Bild entfernen"
                        >
                          <X className="h-3 w-3" />
                        </motion.button>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">
                      Oder URL manuell eingeben:
                    </label>
                    <input
                      type="text"
                      value={formData.preview_image_url}
                      onChange={(e) => {
                        setFormData({ ...formData, preview_image_url: e.target.value })
                        setPreviewImagePreview(null)
                      }}
                      placeholder="/assets/flyer/vorschaubild.jpg"
                      className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Flyer */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Flyer <span className="text-xs text-gray-400">(für Brunch-Detailseite)</span>
                </label>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <motion.label
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 border border-white/10 rounded-lg text-white cursor-pointer hover:bg-gray-800 transition-colors"
                    >
                      <Upload className="h-4 w-4" />
                      <span className="text-sm">
                        {uploadingFlyer ? 'Wird hochgeladen...' : 'Flyer hochladen'}
                      </span>
                      <input
                        ref={flyerFileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'flyer')}
                        disabled={uploadingFlyer}
                        className="hidden"
                      />
                    </motion.label>
                    
                    {formData.flyer_image_url && (
                      <span className="text-xs text-gray-400">
                        {formData.flyer_image_url}
                      </span>
                    )}
                  </div>

                  {(flyerImagePreview || formData.flyer_image_url) && (
                    <div className="relative w-[200px] max-h-[200px] flex-shrink-0">
                      <div className="w-full h-full max-h-[200px] overflow-hidden rounded-lg border border-white/10 bg-gray-900">
                        <img
                          src={(flyerImagePreview && (flyerImagePreview.startsWith('blob:') || flyerImagePreview.startsWith('data:'))) ? flyerImagePreview : getEventImageUrl(formData.flyer_image_url)}
                          alt="Flyer Vorschau"
                          className="w-full h-full object-contain max-h-[200px]"
                        />
                      </div>
                      {formData.flyer_image_url && (
                        <motion.button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, flyer_image_url: '' }))
                            setFlyerImagePreview(null)
                            if (flyerFileInputRef.current) {
                              flyerFileInputRef.current.value = ''
                            }
                          }}
                          className="absolute top-1 right-1 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-full text-white"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Bild entfernen"
                        >
                          <X className="h-3 w-3" />
                        </motion.button>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">
                      Oder URL manuell eingeben:
                    </label>
                    <input
                      type="text"
                      value={formData.flyer_image_url}
                      onChange={(e) => {
                        setFormData({ ...formData, flyer_image_url: e.target.value })
                        setFlyerImagePreview(null)
                      }}
                      placeholder="/assets/flyer/flyer.jpg"
                      className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50 text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Kurzbeschreibung
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50"
                  placeholder="Kurze Beschreibung für die Übersichtsseite"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Ausführliche Beschreibung
                </label>
                <textarea
                  value={formData.long_description}
                  onChange={(e) => setFormData({ ...formData, long_description: e.target.value })}
                  rows={5}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50"
                  placeholder="Detaillierte Beschreibung für die Detailseite"
                />
              </div>

              {/* Features */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Features / Highlights
                  </label>
                  <motion.button
                    type="button"
                    onClick={addFeature}
                    className="text-sm text-brand-400 hover:text-brand-300 flex items-center gap-1 px-3 py-1.5 bg-brand-500/10 hover:bg-brand-500/20 rounded-lg border border-brand-500/20 transition-all"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Plus className="w-4 h-4" />
                    Hinzufügen
                  </motion.button>
                </div>
                <div className="space-y-2">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => updateFeature(index, e.target.value)}
                        className="flex-1 px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50"
                        placeholder="z.B. Romantisches Ambiente mit Kerzen"
                      />
                      <motion.button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg border border-red-500/20 transition-all"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <X className="w-5 h-5" />
                      </motion.button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Kulinarische Highlights – feste Kategorien */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <UtensilsCrossed className="w-5 h-5 text-brand-400" />
                  <label className="block text-sm font-medium text-gray-300">
                    Kulinarische Highlights
                  </label>
                </div>
                <p className="text-xs text-gray-400 mb-4">
                  Jeder Brunch hat die vier Kategorien Frühstück, Vorspeise, Hauptspeise und Dessert. 
                  Wenn nichts angelegt ist, erscheint: „Das genaue Buffet veröffentlichen wir hier ca. einen Monat vor Event.“
                </p>
                <div className="space-y-6">
                  {MENU_CATEGORIES.map(({ key, label }) => (
                    <div key={key} className="p-4 bg-gray-900/30 rounded-xl border border-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-200">{label}</span>
                        <motion.button
                          type="button"
                          onClick={() => addMenuItem(key)}
                          className="text-xs text-brand-400 hover:text-brand-300 flex items-center gap-1 px-2 py-1 bg-brand-500/10 hover:bg-brand-500/20 rounded-lg border border-brand-500/20 transition-all"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Plus className="w-3 h-3" />
                          Hinzufügen
                        </motion.button>
                      </div>
                      <div className="space-y-2">
                        {(formData.menu_highlights[key]?.length ? formData.menu_highlights[key] : ['']).map((item, index) => (
                          <div key={index} className="flex gap-2">
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => updateMenuItem(key, index, e.target.value)}
                              className="flex-1 px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50 text-sm"
                              placeholder={`z.B. ${key === 'fruehstueck' ? 'Croissants, Müsli' : key === 'vorspeise' ? 'Kürbissuppe' : key === 'hauptspeise' ? 'Rührei, Bacon' : 'Obstsalat'}`}
                            />
                            <motion.button
                              type="button"
                              onClick={() => removeMenuItem(key, index)}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg border border-red-500/20 transition-all"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <X className="w-4 h-4" />
                            </motion.button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                <motion.button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false)
                    cancelEdit()
                  }}
                  className="px-6 py-2 bg-white/5 border border-white/20 text-gray-300 rounded-lg hover:bg-white/10 hover:text-white hover:border-white/30 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Abbrechen
                </motion.button>
                <motion.button
                  type="submit"
                  className="flex items-center gap-2 px-8 py-2 bg-gradient-to-r from-brand-600 to-brand-600 border border-brand-500/30 text-white rounded-lg hover:from-brand-500 hover:to-brand-500 transition-all shadow-lg shadow-brand-600/30 font-medium"
                  whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(220, 38, 38, 0.4)' }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Save className="w-5 h-5" />
                  {editingBrunch ? 'Aktualisieren' : 'Erstellen'}
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      )}

      {/* Brunches Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative bg-gradient-to-br from-gray-900/80 to-gray-900/80 backdrop-blur-xl rounded-xl shadow-lg border border-white/10 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />
        
        <div className="relative z-10">
          {isLoading ? (
            <div className="p-12 text-center text-gray-300">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block"
              >
                <Coffee className="h-8 w-8" />
              </motion.div>
              <p className="mt-4">Lade Brunches...</p>
            </div>
          ) : brunches.length === 0 ? (
            <div className="p-12 text-center">
              <Coffee className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-300">Keine Brunches gefunden</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 p-6 min-w-0">
              {brunches.map((brunch, index) => (
                <motion.div
                  key={brunch.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -4 }}
                  className="group relative min-w-0 bg-gradient-to-br from-gray-900/90 to-gray-900/90 backdrop-blur-xl rounded-xl shadow-lg border border-white/10 overflow-hidden hover:border-brand-500/30 transition-all duration-300"
                >
                  {/* Background Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-brand-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  {/* Brunch Image */}
                  {(brunch.preview_image_url || brunch.image_url) && (
                    <div className="relative h-48 overflow-hidden bg-black/50">
                      <img
                        src={getEventImageUrl(brunch.preview_image_url || brunch.image_url) || '/assets/brunch/brunch_vorschau_fallback.jpg'}
                        alt={brunch.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      
                      {/* Highlighted Badge */}
                      {brunch.is_highlighted && (
                        <div className="absolute top-3 left-3">
                          <span className="px-3 py-1 text-xs rounded-full bg-yellow-500/90 text-black font-bold backdrop-blur-sm shadow-lg flex items-center gap-1">
                            <Star className="w-3 h-3 fill-black" />
                            Beliebt
                          </span>
                        </div>
                      )}
                      
                      {/* Status Badge */}
                      <div className="absolute top-3 right-3">
                        <span className={`px-3 py-1 text-xs rounded-full font-medium backdrop-blur-sm shadow-lg ${
                          brunch.is_active
                            ? 'bg-emerald-500/90 text-white'
                            : 'bg-gray-500/90 text-white'
                        }`}>
                          {brunch.is_active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Brunch Info */}
                  <div className="relative z-10 p-5">
                    {/* Title */}
                    <h3 className="text-xl font-semibold text-white mb-2 line-clamp-2 group-hover:text-brand-400 transition-colors">
                      {brunch.title}
                    </h3>
                    
                    {brunch.subtitle && (
                      <p className="text-sm text-brand-400 mb-3">{brunch.subtitle}</p>
                    )}
                    
                    {/* Date & Time */}
                    <div className="flex items-center gap-2 text-gray-300 mb-3">
                      <Calendar className="h-4 w-4 text-brand-400" />
                      <span className="text-sm font-medium">
                        {new Date(brunch.event_date).toLocaleDateString('de-DE')}
                        {brunch.event_time && ` • ${brunch.event_time}`}
                      </span>
                    </div>
                    
                    {/* Description */}
                    {brunch.description && (
                      <p className="text-sm text-gray-300 line-clamp-2 mb-4">
                        {brunch.description}
                      </p>
                    )}
                    
                    {/* Image Status */}
                    <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                      {brunch.preview_image_url && (
                        <span className="px-2 py-1 bg-brand-500/10 text-brand-400 rounded">
                          ✓ Vorschaubild
                        </span>
                      )}
                      {brunch.flyer_image_url && (
                        <span className="px-2 py-1 bg-brand-500/10 text-brand-400 rounded">
                          ✓ Flyer
                        </span>
                      )}
                      {!brunch.preview_image_url && !brunch.flyer_image_url && (
                        <span className="px-2 py-1 bg-gray-500/10 text-gray-400 rounded">
                          ⚠ Keine Bilder
                        </span>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                      <motion.button
                        onClick={() => startEdit(brunch)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-brand-500/10 hover:bg-brand-500/20 text-brand-400 rounded-lg transition-colors text-sm font-medium"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Edit className="h-4 w-4" />
                        Bearbeiten
                      </motion.button>
                      <motion.button
                        onClick={() => brunch.id && handleDelete(brunch.id)}
                        className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title="Löschen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}
